import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import '@/App.css';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Profile from '@/pages/Profile';
import Catalog from '@/pages/Catalog';
import ProgrammingCatalog from '@/pages/ProgrammingCatalog';
import ProgrammingProductDetail from '@/pages/ProgrammingProductDetail';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import MyOrders from '@/pages/MyOrders';
import AdminDashboard from '@/pages/AdminDashboard';
import { Toaster } from '@/components/ui/sonner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);

  // Global handler: on 401 (expired/invalid token) clear session and go to login
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        if (status === 401 && !window.location.pathname.startsWith('/login')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          toast.error('Tu sesión ha caducado. Vuelve a iniciar sesión.');
          setTimeout(() => { window.location.href = '/login'; }, 1200);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Refresh user data from backend
  const refreshUserData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        console.log('✅ User data refreshed:', userData.approved ? 'APPROVED' : 'PENDING');
      } else if (response.status === 401) {
        // Stale/expired token -> clear session and send to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      
      // If user object doesn't have 'approved' field, it's an old session
      // Force user to re-login by clearing localStorage
      if (parsedUser.role === 'client' && parsedUser.approved === undefined) {
        console.log('Old user session detected, clearing localStorage...');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('cart');
        setUser(null);
      } else {
        setUser(parsedUser);
        
        // Refresh user data to get latest approval status
        refreshUserData();
      }
    }
    
    // Load cart from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    
    setLoading(false);
    
    // Set up periodic refresh of user data (every 30 seconds)
    // This ensures approval status is updated without page reload
    const userRefreshInterval = setInterval(() => {
      if (localStorage.getItem('token')) {
        refreshUserData();
      }
    }, 30000); // 30 seconds
    
    // Set up periodic refresh of cart (every 1 second)
    // This ensures cart updates are reflected immediately
    const cartRefreshInterval = setInterval(() => {
      const currentCart = localStorage.getItem('cart');
      if (currentCart) {
        const parsedCart = JSON.parse(currentCart);
        setCart(parsedCart);
      } else {
        // Cart was cleared (e.g., after order completion)
        setCart([]);
      }
    }, 1000); // 1 second
    
    return () => {
      clearInterval(userRefreshInterval);
      clearInterval(cartRefreshInterval);
    };
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart([]);
      }
    };

    window.addEventListener('cartUpdate', handleCartUpdate);
    return () => window.removeEventListener('cartUpdate', handleCartUpdate);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('cart');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing user={user} onLogout={handleLogout} />} />
          <Route 
            path="/login" 
            element={user ? <Navigate to="/catalog" /> : <Login onLogin={handleLogin} />} 
          />
          <Route 
            path="/register" 
            element={user ? <Navigate to="/catalog" /> : <Register onLogin={handleLogin} />} 
          />
          <Route 
            path="/forgot-password" 
            element={user ? <Navigate to="/catalog" /> : <ForgotPassword />} 
          />
          <Route 
            path="/reset-password" 
            element={user ? <Navigate to="/catalog" /> : <ResetPassword />} 
          />
          <Route 
            path="/catalog" 
            element={<Catalog user={user} onLogout={handleLogout} />} 
          />
          <Route 
            path="/programming" 
            element={<ProgrammingCatalog user={user} onLogout={handleLogout} cart={cart} />} 
          />
          <Route 
            path="/programming/:season" 
            element={<ProgrammingCatalog user={user} onLogout={handleLogout} cart={cart} />} 
          />
          <Route 
            path="/programming/product/:id" 
            element={<ProgrammingProductDetail user={user} onLogout={handleLogout} />} 
          />
          <Route 
            path="/product/:id" 
            element={<ProductDetail user={user} onLogout={handleLogout} />} 
          />
          <Route 
            path="/cart" 
            element={user ? <Cart user={user} onLogout={handleLogout} cart={cart} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/my-orders" 
            element={user ? <MyOrders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <Profile user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin" 
            element={
              user && (user.role === 'admin' || user.role === 'manager') ? 
              <AdminDashboard user={user} onLogout={handleLogout} /> : 
              <Navigate to="/" />
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="bottom-right" 
        closeButton={true}
        duration={3000}
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#fff',
            border: '1px solid #334155',
          },
        }}
      />
    </div>
  );
}

export default App;
